/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { isBoolean, isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { MediaControlOrigin } from "../events/PlayerEvent.js";
import { VideoContext } from "../player/Player.js";
import { VideoContextOverrider } from "../player/video/VideoContextOverrider.js";
import { NullAdsController } from "./AdsController.js";
import { AdBreakType } from "./AdUnit.js";


/**
 * This decorator interface for `VideoContext` is used to characterize 
 * video elements that are interleaved by ads.
 * Implementations of this decorator interface hide ad management
 * complexities and operations to the rest of the player components by wrapping the
 * original `VideoContext` object and keeping the same `VideoContext`
 * interface. 
 * 
 */
export interface AdsEnrichedVideoContext extends VideoContext {
    isAdsEnriched: boolean;

    
    //getAdsManager(): google.ima.AdsManager
    destroy(): void;
    startWithAds(forceResume: boolean): Promise<boolean>;
    skipAds(): Promise<boolean>;

}


/**
 * type checker for `AdsEnrichedVideoContext`
 * @param a value to check
 * @returns 
 */
export function isAdsEnrichedVideoContext(a: any): a is AdsEnrichedVideoContext {
    return isObject(a) && 
            ("isAdsEnriched" in a && isBoolean(a.isAdsEnriched)) &&
                ("startWithAds" in a && isFunction(a.startWithAds)) &&
                ("skipAds" in a && isFunction(a.skipAds))
}


/**
 * Null implementation of the wrapping decorator interface `AdsEnrichedVideoContext`
 */
export class NullAdsEnrichedVideoContext extends VideoContextOverrider implements AdsEnrichedVideoContext{


    constructor(private controller: NullAdsController, ctl: VideoContext) {
        super(ctl);
        this.controller.signalResumableMedia(AdBreakType.PREROLL, 
            ctl.ref.info, 
            ctl.ref.executionId)
    }

    public readonly isAdsEnriched: boolean = false;

    destroy(): void {}

    startWithAds(forceResume: boolean): Promise<boolean> {
        return this.originalContext.play(MediaControlOrigin.USER);
    }
    skipAds(): Promise<boolean> {
        return Promise.resolve(true);
    }


}


export class AdsEnrichedVideoContextOverrider extends VideoContextOverrider implements AdsEnrichedVideoContext {
    
    public readonly isAdsEnriched: boolean;

    constructor(protected originalAdsControls: AdsEnrichedVideoContext) {
        super(originalAdsControls)
        this.isAdsEnriched = this.originalAdsControls.isAdsEnriched;
    }


    destroy(): void {
        return this.originalAdsControls.destroy();
    }
    startWithAds(forceResume: boolean): Promise<boolean> {
        return this.originalAdsControls.startWithAds(forceResume);
    }
    skipAds(): Promise<boolean> {
        return this.originalAdsControls.skipAds();
    }

}